import '@sortlist-frontend/design-system/styles.css';
import '_assets/theme/style.scss';
import '@sortlist-frontend/design-system/tailwind.css';

import { CacheProvider, EmotionCache } from '@emotion/react';
import { ApolloProvider, useApollo } from '@sortlist-frontend/data-fetching/public';
import { Provider as DesignSystemProvider } from '@sortlist-frontend/design-system';
import {
  ExperimentPayload,
  FeatureFlagsProvider,
  getServerSidePayload,
  useExtractFeatureFlagOverrides,
} from '@sortlist-frontend/feature-flags';
import { MonitoringProvider } from '@sortlist-frontend/mlm';
import { useTrackingUuid, useTrackPageChange } from '@sortlist-frontend/tracking';
import { withSSRTranslations } from '@sortlist-frontend/translation/ssr';
import App, { type AppContext, type AppInitialProps, type AppProps as NextAppProps } from 'next/app';
import { Inter, Playfair_Display } from 'next/font/google';
import { Router, useRouter } from 'next/router';

import { GlobalSeoTag } from '_components/SEO';
import { extractOrigin } from '_config/domain.config';
import { createEmotionCache } from '_core/nextjs/create-emotion-cache';
import { translationsConfig } from '_core/utils/ssr';

/* istanbul ignore next */
const inter = Inter({
  weight: ['400', '500', '600', '700'],
  subsets: ['latin'],
  variable: '--font-sans-serif',
  display: 'swap',
});

/* istanbul ignore next */
const playfair = Playfair_Display({
  weight: ['400', '700'],
  subsets: ['latin'],
  variable: '--font-serif',
  display: 'swap',
});

// Workaround for https://github.com/zeit/next.js/issues/8592
export type AppProps = NextAppProps & {
  /** Will be defined only is there was an error */
  err?: Error;
  /** Explicit emotion cache required for mui v5 */
  emotionCache?: EmotionCache;
  abTestPayload?: ExperimentPayload;
  gbUuid?: string;
  currentUrl?: string;
};

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

/**
 * @link https://nextjs.org/docs/advanced-features/custom-app
 */
const MyApp = (appProps: AppProps) => {
  const {
    Component,
    pageProps,
    emotionCache = clientSideEmotionCache,
    err,
    abTestPayload,
    gbUuid,
    currentUrl,
  } = appProps;
  const router = useRouter();
  const configOverride = useExtractFeatureFlagOverrides(currentUrl);
  const apolloClient = useApollo({ pageProps, schema: 'public' });
  const { sessionUuid } = useTrackingUuid();

  useTrackPageChange({ app: 'public', locale: pageProps?.locale });

  return (
    <CacheProvider value={emotionCache}>
      <main id="__main" className={`${inter.className} ${playfair.variable}`}>
        <FeatureFlagsProvider
          enableExperiments
          configOverride={configOverride}
          payloadFromServer={abTestPayload}
          gbUuidFromServer={gbUuid}>
          <ApolloProvider client={apolloClient}>
            <DesignSystemProvider
              navigate={router.push as Router['push']}
              className="h-full"
              locale={pageProps?.locale}>
              <MonitoringProvider user={{ id: sessionUuid }}>
                <GlobalSeoTag />
                <Component {...pageProps} err={err} />
              </MonitoringProvider>
            </DesignSystemProvider>
          </ApolloProvider>
        </FeatureFlagsProvider>
      </main>
    </CacheProvider>
  );
};

type PublicAppInitialProps = AppInitialProps & {
  abTestPayload?: ExperimentPayload;
  gbUuid?: string;
  currentUrl?: string;
};

type PublicAppContext = AppContext & {
  ctx: { req?: { cookies?: Record<string, string | undefined> } };
};

MyApp.getInitialProps = async (appContext: PublicAppContext): Promise<PublicAppInitialProps> => {
  const appProps = await App.getInitialProps(appContext);
  const { req } = appContext.ctx;

  if (req == null) {
    return appProps;
  }

  const origin = extractOrigin(req);
  const currentUrl = `${origin}${req.url ?? ''}`;
  const { payload, gbUuid } = await getServerSidePayload(currentUrl, {
    headers: req.headers,
    cookies: req.cookies,
  });

  return {
    ...appProps,
    abTestPayload: payload,
    gbUuid,
    currentUrl,
  };
};

export { MyApp };

export default withSSRTranslations(MyApp, translationsConfig);
